import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { RootState } from 'app/store/rootReducer';
import {
  HNAmount,
  HNExternalBankAccountDetail,
  HNUSBusinessAccountHolder,
  PaidolHighnoteIntegration,
  HNNormalBalance,
  HNClientToken,
  HnViewDirectDepositDetailsQuery,
  HNDirectDepositDetailRestrictedDetails,
  HnFindFinancialAccountTransfersQueryVariables,
  HNExternallyInitiatedACHTransferConnection,
  HNScheduledTransferConnection,
  HNIntegratorInitiatedACHTransferConnection,
  HNIntegratorInitiatedACHTransferEdge,
  HNExternallyInitiatedACHTransferEdge,
  HNScheduledTransferEdge,
  HNBillingCycleConfiguration,
  HNLedger,
  HNAddExternalBankAccountVerifiedThroughFinicityInput,
  HNExternalFinancialBankAccount,
  HNUserError,
  HNAccessDeniedError,
  HNLedgerName,
} from 'API';
// SEE-1232 row o: the superseded auto-payment documents stay in graphql_custom, unreferenced.
// Removing them is SEE-1238's job, and leaving them makes a revert a one-line transport flip.
import { paidolService, SetThresholdAmountInput } from 'app/services/paidol.service';
import { getIntegration } from './reviewOnboardSlice';
import { captureException } from '@sentry/react';
import { highnoteService } from 'app/services/highnote.service';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';

interface ExternalBankAccountDetails extends HNExternalBankAccountDetail {
  name?: string | null;
}

export interface Transactions {
  externalACHTransfers: HNExternallyInitiatedACHTransferConnection;
  incomingScheduledTransfers: HNScheduledTransferConnection;
  integratorACHTransfers: HNIntegratorInitiatedACHTransferConnection;
  financialAccountActivities?: {
    pageInfo?: any;
    edges?: Array<any> | null;
  };
  interFinancialAccountTransfers?: {
    pageInfo?: any;
    edges?: Array<any> | null;
  };
  // New properties for buffered display
  displayedActivityTransactions?: Array<any>;
  hasMoreToDisplay?: boolean;
}

export interface FundingState {
  isLoading?: boolean;
  error?: string | null;
  /**
   * A transfer-list failure, kept apart from `error` so that losing the
   * activity list (or one Load More page) never blanks the whole page, which
   * still shows a valid balance and a working Add Funds.
   */
  transfersError?: string | null;
  financialAccount?: HNDirectDepositDetailRestrictedDetails;
  financialAccountId?: string; // Store the financial account ID for comparisons
  isAddingFundingBankAccount?: boolean;
  fundingBankAccount?: HNExternalFinancialBankAccount | HNUserError | HNAccessDeniedError | null;
  externalBankAccountDetails: ExternalBankAccountDetails;
  directDepositDetailId: string;
  billingCycleConfiguration: HNBillingCycleConfiguration;
  balance?: HNAmount | null;
  creditBalance?: Array<HNLedger | undefined>;
  statementBalance?: HNAmount | null;
  currentAmountDue?: HNAmount | null; // From Highnote statementSnapshot.currentAmountDue
  paymentDueOn?: string | null; // From Highnote latestClosedStatement.paymentDueOn
  pastDueAmount?: HNAmount | null; // From Highnote latestClosedStatement.pastDueAmount
  lastStatementClosedAt?: string | null; // From Highnote latestClosedStatement.closedAt (SEE-1345)
  hasClosedStatement?: boolean; // Whether a latestClosedStatement exists (false for brand-new accounts)
  statements?: Array<any> | null;
  transactions: Transactions;
  externalBankAccountsSummary?: {
    activeIds: string[];
    numActive: number;
    numInactive: number;
  };
  activeExternalBankAccounts: HNExternalFinancialBankAccount[];
  autoPayment?: AutoPaymentRecord | null;
  autoPaymentLoading?: boolean;
}

const initialState: FundingState = {
  isLoading: false,
  error: null,
  transfersError: null,
  isAddingFundingBankAccount: false,
  fundingBankAccount: undefined,
  externalBankAccountDetails: {
    __typename: 'HNExternalBankAccountDetail',
    id: '',
    name: '',
  },
  financialAccount: {
    __typename: 'HNDirectDepositDetailRestrictedDetails',
    number: null,
    routingNumber: null,
    bank: null,
    type: null,
  },
  financialAccountId: undefined,
  directDepositDetailId: '',
  billingCycleConfiguration: {
    id: '',
    __typename: 'HNBillingCycleConfiguration',
    paymentDueDayOfMonth: 0,
    billingCycleStartDayOfMonth: 0,
    billingCycleEndDayOfMonth: 0,
    from: '',
    through: null,
  },
  balance: {
    __typename: 'HNAmount',
    value: 0,
    currencyCode: 'USD',
  },
  statementBalance: {
    __typename: 'HNAmount',
    value: 0,
    currencyCode: 'USD',
  },
  currentAmountDue: null,
  paymentDueOn: null,
  pastDueAmount: null,
  lastStatementClosedAt: null,
  statements: [],
  transactions: {
    externalACHTransfers: {
      __typename: 'HNExternallyInitiatedACHTransferConnection',
      edges: [],
      pageInfo: {
        __typename: 'HNPageInfo',
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: '',
        endCursor: '',
      },
    },
    incomingScheduledTransfers: {
      __typename: 'HNScheduledTransferConnection',
      edges: [],
      pageInfo: {
        __typename: 'HNPageInfo',
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: '',
        endCursor: '',
      },
    },
    integratorACHTransfers: {
      __typename: 'HNIntegratorInitiatedACHTransferConnection',
      edges: [],
      pageInfo: {
        __typename: 'HNPageInfo',
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: '',
        endCursor: '',
      },
    },
    financialAccountActivities: {
      edges: [],
      pageInfo: {
        __typename: 'HNPageInfo',
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: '',
        endCursor: '',
      },
    },
    interFinancialAccountTransfers: {
      edges: [],
      pageInfo: {
        __typename: 'HNPageInfo',
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: '',
        endCursor: '',
      },
    },
  },
  externalBankAccountsSummary: {
    activeIds: [],
    numActive: 0,
    numInactive: 0,
  },
  activeExternalBankAccounts: [],
  autoPayment: null,
  autoPaymentLoading: false,
};

export const getFinancialAccountDetails = createAsyncThunk(
  'cards/funding/getFinancialAccountId',
  async (integration: PaidolHighnoteIntegration, { rejectWithValue, dispatch, getState }) => {
    try {
      const { businessAccountHolderId, externalFinancialBankAccountId, financialAccountId } = integration;

      // Validate required ID with more specific error handling
      if (!businessAccountHolderId || businessAccountHolderId.trim() === '') {
        return rejectWithValue('MISSING_BUSINESS_ACCOUNT_HOLDER_ID');
      }

      const accountHolderResult = await highnoteService.hnGetBusinessAccountHolder({
        id: businessAccountHolderId,
      });

      if (!accountHolderResult) {
        return rejectWithValue('Account data not found. Please check your permissions or try again later.');
      }

      const accountHolder = accountHolderResult as HNUSBusinessAccountHolder;

      const externalFinancialBankAccount = accountHolder.externalFinancialAccounts?.edges?.find(
        (edge) => edge.node?.accountStatus === 'ACTIVE' && edge.node?.id === externalFinancialBankAccountId
      )?.node as HNExternalFinancialBankAccount;

      // Summarize all external bank accounts
      const allExternalAccounts =
        accountHolder.externalFinancialAccounts?.edges?.map((edge) => edge.node) || [];
      const activeExternalAccounts = allExternalAccounts.filter(
        (acc) => acc?.accountStatus === 'ACTIVE'
      ) as HNExternalFinancialBankAccount[];
      const inactiveExternalAccounts = allExternalAccounts.filter(
        (acc) => acc?.accountStatus !== 'ACTIVE'
      ) as HNExternalFinancialBankAccount[];

      const financialAccount = accountHolder.financialAccounts?.edges?.find(
        (edge) => edge.node?.accountStatus === 'ACTIVE' && edge.node?.id === financialAccountId
      )?.node;

      const ledger = financialAccount?.ledgers?.find((l) => l.name === HNLedgerName.AVAILABLE_CASH);

      // Get credit data from Paidol instead of HighNote
      const state = getState() as RootState;
      const { selectedPaidol } = selectUserCompanies(state);
      const paidolCreditLimit = Number(selectedPaidol?.creditLimit) || 0;

      // Get outstanding balance ledger from HighNote (this is the actual debt, used for display)
      const outstandingBalancePayable = financialAccount?.ledgers?.find(
        (l) => l.name === HNLedgerName.OUTSTANDING_BALANCE_PAYABLE
      );

      // Get HN's credit limit and available credit (which includes pending transactions)
      const hnCreditLimitLedger = financialAccount?.ledgers?.find(
        (l) => l.name === HNLedgerName.ACCOUNT_HOLDER_CREDIT_LIMIT
      );
      const hnAvailableCreditLedger = financialAccount?.ledgers?.find(
        (l) => l.name === HNLedgerName.AVAILABLE_CREDIT
      );
      const hnCreditLimit = Number(hnCreditLimitLedger?.creditBalance?.value) || 0;
      const hnAvailableCredit = Number(hnAvailableCreditLedger?.creditBalance?.value) || 0;

      // Calculate used credit from HN (includes pending transactions)
      // usedCredit = HN Credit Limit - HN Available Credit
      const usedCredit = hnCreditLimit - hnAvailableCredit;

      // Calculate available credit: Our DB Credit Limit - Used Credit (from HN, includes pending)
      // Cap at 0 - never show negative available credit
      const availableCredit = Math.max(0, paidolCreditLimit - usedCredit);

      // Create credit ledger structure using Paidol data
      // IMPORTANT: Array must always have exactly 3 elements in order:
      // [0] = Available Credit, [1] = Outstanding Balance, [2] = Credit Limit
      // Components destructure this array by position, so we must always include
      // the outstanding balance entry (with 0 value if not present from Highnote)
      const creditLedger = [
        // Available Credit (calculated from Paidol limit - outstanding balance)
        {
          id: 'paidol-available-credit',
          name: HNLedgerName.AVAILABLE_CREDIT,
          normalBalance: HNNormalBalance.CREDIT,
          creditBalance: {
            value: availableCredit,
            currencyCode: 'USD',
            __typename: 'HNAmount' as const,
          },
          debitBalance: {
            value: 0,
            currencyCode: 'USD',
            __typename: 'HNAmount' as const,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          __typename: 'HNLedger' as const,
        },
        // Outstanding Balance Payable (use HighNote data if available, otherwise default to 0)
        outstandingBalancePayable ?? {
          id: 'default-outstanding-balance',
          name: HNLedgerName.OUTSTANDING_BALANCE_PAYABLE,
          normalBalance: HNNormalBalance.CREDIT,
          creditBalance: {
            value: 0,
            currencyCode: 'USD',
            __typename: 'HNAmount' as const,
          },
          debitBalance: {
            value: 0,
            currencyCode: 'USD',
            __typename: 'HNAmount' as const,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          __typename: 'HNLedger' as const,
        },
        // Credit Limit (from Paidol)
        {
          id: 'paidol-credit-limit',
          name: HNLedgerName.ACCOUNT_HOLDER_CREDIT_LIMIT,
          normalBalance: HNNormalBalance.CREDIT,
          creditBalance: {
            value: paidolCreditLimit,
            currencyCode: 'USD',
            __typename: 'HNAmount' as const,
          },
          debitBalance: {
            value: 0,
            currencyCode: 'USD',
            __typename: 'HNAmount' as const,
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          __typename: 'HNLedger' as const,
        },
      ];

      const externalBankAccountDetails =
        externalFinancialBankAccount?.externalBankAccountDetails as HNExternalBankAccountDetail;

      const bankAccountDetails = {
        ...externalBankAccountDetails,
        name: externalFinancialBankAccount?.name,
      };

      // Gather statements for history (UI uses this in the info dialog)
      const statements = financialAccount?.statements?.edges?.map((edge) => edge.node) || [];

      // Use only the latest closed statement's unpaid balance for side-panel display
      const snapshotLatestClosed = financialAccount?.statementSnapshot?.latestClosedStatement;
      const finalStatementBalance = snapshotLatestClosed?.endingPrimaryCreditBalance ?? null;

      // Highnote-provided amount due and payment info
      // These fields exist on concrete statement snapshot/statement types but not the base interface,
      // so we access them via the snapshot object directly
      const snapshot = financialAccount?.statementSnapshot as any;
      const currentAmountDue = snapshot?.currentAmountDue ?? null;
      const snapshotCurrentOpen = financialAccount?.statementSnapshot?.currentOpenStatement;
      // Prefer latestClosedStatement.paymentDueOn; fall back to currentOpenStatement for new accounts
      const paymentDueOn =
        (snapshotLatestClosed as any)?.paymentDueOn ?? (snapshotCurrentOpen as any)?.paymentDueOn ?? null;
      const pastDueAmount = (snapshotLatestClosed as any)?.pastDueAmount ?? null;

      return {
        externalFinancialBankAccount: externalFinancialBankAccount,
        externalBankAccountDetails: bankAccountDetails,
        financialAccountId: financialAccountId, // Store the ID for comparison
        directDepositDetailId: financialAccount?.directDepositDetails?.id,
        billingCycleConfiguration: financialAccount?.activeBillingCycleConfiguration,
        balance:
          ledger?.normalBalance === HNNormalBalance.CREDIT ? ledger?.creditBalance : ledger?.debitBalance,
        creditBalance: creditLedger,
        statementBalance: finalStatementBalance,
        currentAmountDue,
        paymentDueOn,
        pastDueAmount,
        lastStatementClosedAt: snapshotLatestClosed?.closedAt ?? null,
        hasClosedStatement: !!snapshotLatestClosed,
        statements: statements,
        externalBankAccountsSummary: {
          activeIds: activeExternalAccounts.map((a) => a.id as string).filter(Boolean),
          numActive: activeExternalAccounts.length,
          numInactive: inactiveExternalAccounts.length,
        },
        activeExternalBankAccounts: activeExternalAccounts,
      };
    } catch (error: unknown) {
      captureException(error);

      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }

      // Try to extract more details from the error
      if (typeof error === 'object' && error !== null) {
        const errorObj = error as any;
        if (errorObj.message) {
          return rejectWithValue(errorObj.message);
        }
        if (errorObj.errors && Array.isArray(errorObj.errors)) {
          return rejectWithValue(errorObj.errors.map((e: any) => e.message).join(', '));
        }
      }

      return rejectWithValue('An unknown error occurred');
    }
  }
);

export const getAllFinancialAccountTransfers = createAsyncThunk(
  'cards/funding/getAllFinancialAccountTransfers',
  async (
    {
      id,
      scheduledTransfersAfterCursor = undefined,
      integratorTransfersAfterCursor = undefined,
      externalTransfersAfterCursor = undefined,
      firstScheduledTransfers = 20,
      firstIntegratorTransfers = 20,
      firstExternalTransfers = 20,
    }: HnFindFinancialAccountTransfersQueryVariables & {
      firstScheduledTransfers?: number;
      firstIntegratorTransfers?: number;
      firstExternalTransfers?: number;
    },
    { dispatch, rejectWithValue }
  ) => {
    // Validate ID before making GraphQL call
    if (!id || id.trim() === '') {
      return rejectWithValue('MISSING_FINANCIAL_ACCOUNT_ID');
    }

    try {
      const result = await highnoteService.hnFindFinancialAccountTransfers({
        id,
        firstScheduledTransfers,
        scheduledTransfersAfterCursor,
        firstIntegratorTransfers,
        integratorTransfersAfterCursor,
        firstExternalTransfers,
        externalTransfersAfterCursor,
      });

      if (!result) {
        return rejectWithValue('Transfer data not found. Please check your permissions or try again later.');
      }

      return result as unknown as Transactions;
    } catch (error: unknown) {
      captureException(error);

      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }

      // Try to extract more details from the error
      if (typeof error === 'object' && error !== null) {
        const errorObj = error as any;
        if (errorObj.message) {
          return rejectWithValue(errorObj.message);
        }
        if (errorObj.errors && Array.isArray(errorObj.errors)) {
          return rejectWithValue(errorObj.errors.map((e: any) => e.message).join(', '));
        }
      }

      return rejectWithValue('An unknown error occurred while fetching transfers');
    }
  }
);

export const getFinancialAccountActivities = createAsyncThunk(
  'funding/getActivities',
  async (
    {
      financialAccountId,
      statementClosedDate,
      activitiesAfterCursor,
    }: {
      financialAccountId: string;
      statementClosedDate?: string;
      activitiesAfterCursor?: string;
    },
    { dispatch, rejectWithValue }
  ) => {
    // Validate ID before making GraphQL call
    if (!financialAccountId || financialAccountId.trim() === '') {
      return rejectWithValue('MISSING_FINANCIAL_ACCOUNT_ID');
    }

    try {
      const variables: any = {
        id: financialAccountId,
        firstActivities: 20, // Changed to 20 to match other tabs
      };

      if (statementClosedDate) {
        variables.filterBy = {
          createdAt: {
            greaterThan: statementClosedDate,
          },
        };
      }

      if (activitiesAfterCursor) {
        variables.activitiesAfterCursor = activitiesAfterCursor;
      }

      const result = await highnoteService.hnFindFinancialAccountActivities(variables);
      return result ?? null;
    } catch (error) {
      captureException(error);
      return rejectWithValue(error);
    }
  }
);

export const getInterFinancialAccountTransfers = createAsyncThunk(
  'funding/getInterFinancialAccountTransfers',
  async (
    {
      financialAccountId,
      activitiesAfterCursor,
    }: {
      financialAccountId: string;
      activitiesAfterCursor?: string;
    },
    { dispatch, rejectWithValue }
  ) => {
    // Validate ID before making GraphQL call
    if (!financialAccountId || financialAccountId.trim() === '') {
      return rejectWithValue('MISSING_FINANCIAL_ACCOUNT_ID');
    }

    try {
      /**
       * Keep the transfers that actually moved money for this customer.
       *
       * `purpose` is the discriminator. Highnote sets it only on an
       * InterFinancialAccountTransfer, and only for the kinds that are
       * payments: across 207 internal transfers on live accounts it is
       * REPAYMENT or GENERAL for real money movements and null for
       * SET_LIMIT, the DISPUTE_* family and CLOSE_LINE_OF_CREDIT. Every
       * other source kind has no purpose field at all, so it reads null too.
       *
       * This used to test `source.__typename === 'HNInterFinancialAccountTransfer'`,
       * a name that no longer exists: after the Amplify to CDK migration
       * AppSync answers with its own type, HNActivitySource. The comparison
       * could never be true, so every internal transfer was dropped and no
       * repayment or manually applied credit has appeared in payment activity
       * since 2026-04-02 (SEE-1638).
       */
      const isVisibleTransfer = (edge: any): boolean => {
        const source = edge?.node?.source;
        const purpose = source?.purpose ?? null;
        if (purpose === null) return false;
        const memo = (source?.memo || '').toUpperCase().trim();
        if (purpose === 'ADJUSTMENT' && memo.startsWith('INTERNAL')) return false;
        return true;
      };

      const mapEdge = (edge: any) => ({
        cursor: edge.cursor,
        node: {
          // Our own shape, named for what it represents. AppSync returns
          // HNActivitySource for every activity source regardless of kind, so
          // the table cannot learn the kind from the payload; this is the
          // marker it reads.
          __typename: 'HNInterFinancialAccountTransfer',
          id: edge.node.source?.id,
          createdAt: edge.node.createdAt,
          memo: edge.node.source?.memo || '',
          amount: edge.node.postedAmount || edge.node.pendingAmount || null,
          // Falling back to pendingAmount is fine, but the row must not then
          // claim the money already moved.
          isPending: !edge.node.postedAmount && !!edge.node.pendingAmount,
          source: edge.node.source,
        },
      });

      // Highnote doesn't support server-side purpose filtering, so dispute IFA
      // transfers (purpose=null) can be interspersed with real transfers. We
      // exhaust all pages so the stored hasNextPage is accurate — otherwise
      // the Load More button appears but yields only filtered-out disputes.
      const PAGE_SIZE = 20;
      const MAX_AUTO_PAGES = 10; // Safety limit
      let allVisibleEdges: any[] = [];
      let cursor = activitiesAfterCursor;
      let lastPageInfo: any = {};

      for (let page = 0; page < MAX_AUTO_PAGES; page++) {
        const variables: any = {
          id: financialAccountId,
          firstActivities: PAGE_SIZE,
          filterBy: {
            activityType: { equals: 'INTERNAL_TRANSFER' },
          },
        };
        if (cursor) {
          variables.activitiesAfterCursor = cursor;
        }

        const activityData = await highnoteService.hnFindFinancialAccountActivities(variables);
        if (!activityData) {
          return rejectWithValue('Inter-financial account transfer data not found');
        }

        const rawEdges = activityData.financialAccountActivities?.edges || [];
        lastPageInfo = activityData.financialAccountActivities?.pageInfo || {};

        const visibleEdges = rawEdges.filter(isVisibleTransfer).map(mapEdge);
        allVisibleEdges = allVisibleEdges.concat(visibleEdges);

        if (!lastPageInfo.hasNextPage) break;

        cursor = lastPageInfo.endCursor;
      }

      return {
        interFinancialAccountTransfers: {
          pageInfo: {
            ...lastPageInfo,
            hasNextPage: lastPageInfo.hasNextPage || false,
            endCursor: lastPageInfo.endCursor || null,
          },
          edges: allVisibleEdges,
        },
      };
    } catch (error) {
      captureException(error);
      return rejectWithValue(error);
    }
  }
);

// NOTE: getPaymentsAfterStatementClose thunk was removed — statement balance now uses
// Highnote's currentAmountDue directly. See git history for the old manual calculation logic.

export const viewDirectDepositRestrictedDetails = createAsyncThunk(
  'cards/funding/viewDirectDepositRestrictedDetails',
  async (directDepositDetailId: string) => {
    const clientToken = (await highnoteService.hnGenerateDirectDepositDetailClientToken({
      directDepositDetailId,
      permissions: ['READ_RESTRICTED_DETAILS'],
    })) as HNClientToken;

    const response = (await highnoteService.hnViewDirectDepositDetails({
      id: directDepositDetailId,
      _bearerToken: clientToken.value,
    })) as HnViewDirectDepositDetailsQuery['hnViewDirectDepositDetails'];

    if (response?.restrictedDetails?.__typename === 'HNDirectDepositDetailRestrictedDetails') {
      return response.restrictedDetails;
    }
    return null;
  }
);

export const saveThresholdAmount = createAsyncThunk(
  'cards/funding/saveThresholdAmount',
  async (input: SetThresholdAmountInput) => {
    return paidolService.setThresholdAmount({
      paidolId: input.paidolId,
      lowBalanceAlertThreshold: input.lowBalanceAlertThreshold ?? undefined,
      lowBalanceAutoFundingAmount: input.lowBalanceAutoFundingAmount ?? undefined,
      lowBalanceAutoFundingThreshold: input.lowBalanceAutoFundingThreshold ?? undefined,
    });
  }
);

export interface AddFundingBankAccountArgs {
  paidolId: string;
  input: HNAddExternalBankAccountVerifiedThroughFinicityInput;
}

export const addFundingBankAccount = createAsyncThunk(
  'cards/funding/addFundingBankAccount',
  async ({ paidolId, input }: AddFundingBankAccountArgs, { dispatch }) => {
    const response = await highnoteService.hnAddExternalBankAccountVerifiedThroughFinicity(input);
    if ((response as HNExternalFinancialBankAccount)?.id !== undefined && paidolId) {
      dispatch(getIntegration(paidolId));
    }
    return response;
  }
);

export interface AddExternalBankAccountViaPlaidArgs {
  accountHolderId: string;
  processorToken: string;
}

/**
 * Adds a bank account to Highnote using a Plaid processor token.
 * This is used for the credit card repayment flow where users verify
 * their bank accounts via Plaid (instant auth or micro-deposits).
 */
export const addExternalBankAccountViaPlaid = createAsyncThunk(
  'cards/funding/addExternalBankAccountViaPlaid',
  async ({ accountHolderId, processorToken }: AddExternalBankAccountViaPlaidArgs) => {
    const response = await highnoteService.hnAddExternalBankAccountVerifiedThroughPlaid({
      accountHolderId,
      externalToken: {
        value: processorToken,
      },
    });

    // Check for errors
    if ((response as HNUserError)?.errors) {
      const errorMessages = (response as HNUserError).errors?.map((e) => e.description).join(', ');
      throw new Error(errorMessages || 'Failed to add bank account to Highnote');
    }

    if ((response as HNAccessDeniedError)?.message) {
      throw new Error((response as HNAccessDeniedError).message || 'Access denied');
    }

    // Return the successfully added account
    if ((response as HNExternalFinancialBankAccount)?.id) {
      return response as HNExternalFinancialBankAccount;
    }

    throw new Error('Unexpected response from Highnote');
  }
);

export const disconnectExternalFinancialAccount = createAsyncThunk(
  'cards/funding/disconnectExternalFinancialAccount',
  async (financialAccountId: string) => {
    return highnoteService.hnCloseExternalFinancialBankAccount({
      externalFinancialBankAccountId: financialAccountId,
    });
  }
);

// --- AutoPayment Thunks ---

export interface AutoPaymentRecord {
  id: string;
  paidolId: string;
  amountType: 'STATEMENT_BALANCE' | 'FIXED_AMOUNT';
  fixedAmountCents?: number | null;
  paymentDayOfMonth: number;
  externalBankAccountId: string;
  financialAccountId: string;
  active: boolean;
  createdBy: string;
  updatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAutoPaymentArgs {
  paidolId: string;
  amountType: 'STATEMENT_BALANCE' | 'FIXED_AMOUNT';
  fixedAmountCents?: number;
  paymentDayOfMonth: number;
  externalBankAccountId: string;
  financialAccountId: string;
  createdBy: string;
}

export const createAutoPayment = createAsyncThunk(
  'cards/funding/createAutoPayment',
  async (args: CreateAutoPaymentArgs) => {
    // SEE-1232 row o: transport flip. `authPaidolId` is dropped — CDK's
    // CreateAutoPaymentInput does not accept it and the server derives the tenant key,
    // which also stops a row being moved between companies.
    return paidolService.createAutoPayment<AutoPaymentRecord>({
      paidolId: args.paidolId,
      amountType: args.amountType,
      fixedAmountCents: args.fixedAmountCents ?? null,
      paymentDayOfMonth: args.paymentDayOfMonth,
      externalBankAccountId: args.externalBankAccountId,
      financialAccountId: args.financialAccountId,
      active: true,
      createdBy: args.createdBy,
    });
  }
);

export const deactivateAutoPayment = createAsyncThunk(
  'cards/funding/deactivateAutoPayment',
  async ({ id, updatedBy }: { id: string; updatedBy: string }) => {
    // SEE-1232 row o: transport flip only.
    return paidolService.updateAutoPayment<AutoPaymentRecord>({ id, active: false, updatedBy });
  }
);

export const fetchAutoPaymentByPaidolId = createAsyncThunk(
  'cards/funding/fetchAutoPaymentByPaidolId',
  async (paidolId: string) => {
    // SEE-1232 row o: transport flip only — same "first active row" selection.
    const items = await paidolService.listAutoPaymentsByPaidolId<AutoPaymentRecord>(paidolId);
    return items.find((item) => item.active) ?? null;
  }
);

type ObjArray =
  | Array<HNIntegratorInitiatedACHTransferEdge>
  | Array<HNExternallyInitiatedACHTransferEdge>
  | Array<HNScheduledTransferEdge>;

function findUniques(arr1: ObjArray, arr2: ObjArray): ObjArray {
  const array = [...arr1, ...arr2];
  const result = Object.values(array.reduce((acc, obj) => ({ ...acc, [obj.node?.id ?? '']: obj }), {}));
  return result as ObjArray;
}

const fundingSlice = createSlice({
  name: 'cards/funding',
  initialState,
  reducers: {
    resetFundingSlice: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getFinancialAccountDetails.pending, (state, action) => {
      state.isLoading = true;
    });

    builder.addCase(getFinancialAccountDetails.fulfilled, (state, action) => {
      if (action.payload) {
        state.fundingBankAccount = action.payload.externalFinancialBankAccount;
        state.financialAccountId = action.payload.financialAccountId ?? initialState.financialAccountId;
        state.externalBankAccountDetails =
          action.payload.externalBankAccountDetails ?? initialState.externalBankAccountDetails;
        state.externalBankAccountsSummary =
          action.payload.externalBankAccountsSummary ?? initialState.externalBankAccountsSummary;
        state.activeExternalBankAccounts =
          action.payload.activeExternalBankAccounts ?? initialState.activeExternalBankAccounts;
        state.billingCycleConfiguration =
          action.payload?.billingCycleConfiguration ?? initialState.billingCycleConfiguration;
        state.directDepositDetailId =
          action.payload?.directDepositDetailId ?? initialState.directDepositDetailId;
        state.balance = action.payload.balance ?? initialState.balance;
        state.creditBalance = action.payload.creditBalance ?? initialState.creditBalance;
        state.statementBalance = action.payload.statementBalance ?? initialState.statementBalance;
        state.currentAmountDue = action.payload.currentAmountDue ?? initialState.currentAmountDue;
        state.paymentDueOn = action.payload.paymentDueOn ?? initialState.paymentDueOn;
        state.pastDueAmount = action.payload.pastDueAmount ?? initialState.pastDueAmount;
        state.lastStatementClosedAt =
          action.payload.lastStatementClosedAt ?? initialState.lastStatementClosedAt;
        state.hasClosedStatement = action.payload.hasClosedStatement ?? false;
        state.statements = action.payload.statements ?? initialState.statements;
      }
      state.isLoading = false;
      state.error = null; // Clear any previous errors
    });

    builder.addCase(getFinancialAccountDetails.rejected, (state, action) => {
      state.isLoading = false;
      state.error = (action.payload as string) || action.error.message || 'Failed to fetch account data';
    });

    builder.addCase(viewDirectDepositRestrictedDetails.fulfilled, (state, action) => {
      if (action.payload) {
        state.financialAccount = action.payload ?? initialState.financialAccount;
      }
    });
    builder.addCase(getAllFinancialAccountTransfers.pending, (state) => {
      state.transfersError = null;
    });

    builder.addCase(getAllFinancialAccountTransfers.rejected, (state, action) => {
      state.transfersError =
        (action.payload as string) || action.error.message || 'Failed to fetch transfers';
    });

    builder.addCase(getAllFinancialAccountTransfers.fulfilled, (state, action) => {
      if (action.payload) {
        // Get the original request parameters to determine what was requested
        const requestParams = action.meta.arg;
        const firstIntegrator = requestParams.firstIntegratorTransfers || 20;
        const firstExternal = requestParams.firstExternalTransfers || 20;
        const firstScheduled = requestParams.firstScheduledTransfers || 20;

        /**
         * One query returns all three connections, and the page size applies to
         * each of them, so a request meant to advance ONE connection also brings
         * back page 1 of the others. Writing those back would move their cursor
         * to the end of page 1.
         *
         * That is what made Load More stop working: the page fires a request per
         * connection in parallel, and whichever landed last reset the others'
         * cursors. The next click refetched a page already held, the merge
         * deduped it, and no row appeared while the button stayed. So on a
         * paginated request, only touch the connections actually being paged.
         */
        const pagingIntegrator = !!requestParams.integratorTransfersAfterCursor;
        const pagingExternal = !!requestParams.externalTransfersAfterCursor;
        const pagingScheduled = !!requestParams.scheduledTransfersAfterCursor;
        const isPaginatedRequest = pagingIntegrator || pagingExternal || pagingScheduled;

        if (action.payload?.integratorACHTransfers && (!isPaginatedRequest || pagingIntegrator)) {
          const pageInfo = { ...action.payload.integratorACHTransfers.pageInfo };
          const receivedCount = action.payload.integratorACHTransfers.edges?.length || 0;

          // Correct hasNextPage if we received fewer items than requested (for initial loads)
          if (receivedCount < firstIntegrator && !requestParams.integratorTransfersAfterCursor) {
            pageInfo.hasNextPage = false;
          }

          state.transactions.integratorACHTransfers.pageInfo = pageInfo;

          if (
            state.transactions.integratorACHTransfers.edges &&
            action.payload?.integratorACHTransfers?.edges
          ) {
            state.transactions.integratorACHTransfers.edges = findUniques(
              state.transactions.integratorACHTransfers.edges,
              action.payload.integratorACHTransfers.edges
            ) as Array<HNIntegratorInitiatedACHTransferEdge>;
          }
        }

        if (action.payload?.externalACHTransfers && (!isPaginatedRequest || pagingExternal)) {
          const pageInfo = { ...action.payload.externalACHTransfers.pageInfo };
          const receivedCount = action.payload.externalACHTransfers.edges?.length || 0;

          // Correct hasNextPage if we received fewer items than requested (for initial loads)
          if (receivedCount < firstExternal && !requestParams.externalTransfersAfterCursor) {
            pageInfo.hasNextPage = false;
          }

          state.transactions.externalACHTransfers.pageInfo = pageInfo;

          if (state.transactions.externalACHTransfers.edges && action.payload?.externalACHTransfers?.edges) {
            state.transactions.externalACHTransfers.edges = findUniques(
              state.transactions.externalACHTransfers.edges,
              action.payload.externalACHTransfers.edges
            ) as Array<HNExternallyInitiatedACHTransferEdge>;
          }
        }

        if (action.payload?.incomingScheduledTransfers && (!isPaginatedRequest || pagingScheduled)) {
          const pageInfo = { ...action.payload.incomingScheduledTransfers.pageInfo };
          const receivedCount = action.payload.incomingScheduledTransfers.edges?.length || 0;

          // Correct hasNextPage if we received fewer items than requested (for initial loads)
          if (receivedCount < firstScheduled && !requestParams.scheduledTransfersAfterCursor) {
            pageInfo.hasNextPage = false;
          }

          state.transactions.incomingScheduledTransfers.pageInfo = pageInfo;

          if (
            state.transactions.incomingScheduledTransfers.edges &&
            action.payload?.incomingScheduledTransfers?.edges
          ) {
            state.transactions.incomingScheduledTransfers.edges = findUniques(
              state.transactions.incomingScheduledTransfers.edges,
              action.payload.incomingScheduledTransfers.edges
            ) as Array<HNScheduledTransferEdge>;
          }
        }
      }
    });

    builder.addCase(disconnectExternalFinancialAccount.fulfilled, (state, action) => {
      if ((action.payload as HNExternalFinancialBankAccount)?.id) {
        state.fundingBankAccount = undefined;
      }
    });

    builder.addCase(addFundingBankAccount.pending, (state, action) => {
      state.isAddingFundingBankAccount = true;
    });

    // Handle financial account activities
    builder.addCase(getFinancialAccountActivities.fulfilled, (state, action) => {
      if (action.payload && action.payload.financialAccountActivities) {
        // Ensure financialAccountActivities exists
        if (!state.transactions.financialAccountActivities) {
          state.transactions.financialAccountActivities = {
            pageInfo: null,
            edges: [],
          };
        }

        // Update page info
        state.transactions.financialAccountActivities.pageInfo =
          action.payload.financialAccountActivities.pageInfo;

        // Merge activities to support pagination
        if (
          state.transactions.financialAccountActivities.edges &&
          action.payload.financialAccountActivities.edges
        ) {
          const currentEdges = state.transactions.financialAccountActivities.edges;
          const newEdges = action.payload.financialAccountActivities.edges;
          // Efficient merge - dedupe by ID using Map (O(N) instead of O(N²))
          const byId = new Map<string, any>();
          for (const e of currentEdges) if (e?.node?.id) byId.set(e.node.id, e);
          for (const e of newEdges) if (e?.node?.id && !byId.has(e.node.id)) byId.set(e.node.id, e);
          state.transactions.financialAccountActivities.edges = Array.from(byId.values());
        } else {
          state.transactions.financialAccountActivities.edges =
            action.payload.financialAccountActivities.edges || [];
        }
      } else {
        // No financial account activities in payload
      }
    });

    // Handle inter-financial account transfers
    builder.addCase(getInterFinancialAccountTransfers.fulfilled, (state, action) => {
      if (action.payload && action.payload.interFinancialAccountTransfers) {
        // Ensure interFinancialAccountTransfers exists
        if (!state.transactions.interFinancialAccountTransfers) {
          state.transactions.interFinancialAccountTransfers = {
            pageInfo: null,
            edges: [],
          };
        }

        // Use server-side pageInfo directly — Highnote's hasNextPage is accurate since we
        // filter by activityType server-side. No client-side override needed.
        state.transactions.interFinancialAccountTransfers.pageInfo =
          action.payload.interFinancialAccountTransfers.pageInfo;

        // Merge transfers to support pagination
        if (
          state.transactions.interFinancialAccountTransfers.edges &&
          action.payload.interFinancialAccountTransfers.edges
        ) {
          const currentEdges = state.transactions.interFinancialAccountTransfers.edges;
          const newEdges = action.payload.interFinancialAccountTransfers.edges;
          // Efficient merge - dedupe by ID using Map (O(N) instead of O(N²))
          const byId = new Map<string, any>();
          for (const e of currentEdges) if (e?.node?.id) byId.set(e.node.id, e);
          for (const e of newEdges) if (e?.node?.id && !byId.has(e.node.id)) byId.set(e.node.id, e);
          state.transactions.interFinancialAccountTransfers.edges = Array.from(byId.values());
        } else {
          state.transactions.interFinancialAccountTransfers.edges =
            action.payload.interFinancialAccountTransfers.edges || [];
        }
      }
    });

    // AutoPayment reducers
    builder.addCase(fetchAutoPaymentByPaidolId.pending, (state) => {
      state.autoPaymentLoading = true;
    });
    builder.addCase(fetchAutoPaymentByPaidolId.fulfilled, (state, action) => {
      state.autoPayment = action.payload;
      state.autoPaymentLoading = false;
    });
    builder.addCase(fetchAutoPaymentByPaidolId.rejected, (state) => {
      state.autoPaymentLoading = false;
    });
    builder.addCase(createAutoPayment.fulfilled, (state, action) => {
      state.autoPayment = action.payload ?? null;
    });
    builder.addCase(deactivateAutoPayment.fulfilled, (state) => {
      state.autoPayment = null;
    });

    builder.addMatcher(
      isAnyOf(addFundingBankAccount.fulfilled, addFundingBankAccount.rejected),
      (state, action) => {
        state.isAddingFundingBankAccount = false;
        if (action.payload) {
          state.fundingBankAccount = action.payload as
            | HNExternalFinancialBankAccount
            | HNUserError
            | HNAccessDeniedError
            | null;
        }
      }
    );
  },
});

export const { resetFundingSlice } = fundingSlice.actions;

export const selectFundingSlice = (state: RootState): FundingState => state?.cards?.funding ?? initialState;

export default fundingSlice.reducer;
